/**
 * Secure Links Table Component
 * TASK-361: Document Management & Compliance System
 *
 * Displays a table of all secure upload links created for clients,
 * with actions to view, revoke, and resend email.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Link2,
  RefreshCw,
  Loader2,
  AlertCircle,
  Plus,
  Copy,
  Check,
  Mail,
  Trash2,
  User,
  Clock,
  MoreVertical,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  SecureUploadLink,
  getUploadLinkStatusColor,
  getUploadLinkStatusLabel,
  getDocTypeLabel,
} from '../../types/documentCompliance';
import { secureUploadLinkService } from '../../services/secureUploadLinkService';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface SecureLinksTableProps {
  clientId?: string;
  onCreateLink?: () => void;
}

const SecureLinksTable: React.FC<SecureLinksTableProps> = ({
  clientId,
  onCreateLink,
}) => {
  const navigate = useNavigate();
  const [links, setLinks] = useState<SecureUploadLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Action states
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Dropdown menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Fetch links
  const fetchLinks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await secureUploadLinkService.listLinks({
        client_id: clientId,
        status: statusFilter === 'all' ? undefined : statusFilter,
      });

      setLinks(response.links);
      setTotal(response.total);
    } catch (err) {
      console.error('Failed to fetch secure links:', err);
      setError('Failed to load upload links');
    } finally {
      setLoading(false);
    }
  }, [clientId, statusFilter]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  // Copy link URL
  const handleCopyUrl = async (link: SecureUploadLink) => {
    if (!link.portal_url) return;

    try {
      await navigator.clipboard.writeText(link.portal_url);
      setCopiedLinkId(link.id);
      setTimeout(() => setCopiedLinkId(null), 2000);
      toast.success('Link copied to clipboard');
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy link');
    }
  };

  // Resend email
  const handleResendEmail = async (linkId: string) => {
    try {
      setActionLoading(linkId);
      await secureUploadLinkService.resendEmail(linkId);
      toast.success('Email sent successfully');
    } catch (err) {
      console.error('Failed to resend email:', err);
      toast.error('Failed to send email');
    } finally {
      setActionLoading(null);
      setOpenMenuId(null);
    }
  };

  // Revoke link
  const handleRevokeLink = async (linkId: string) => {
    if (!confirm('Are you sure you want to revoke this link? The client will no longer be able to upload documents.')) {
      return;
    }

    try {
      setActionLoading(linkId);
      await secureUploadLinkService.revokeLink(linkId);
      toast.success('Link revoked successfully');
      fetchLinks();
    } catch (err) {
      console.error('Failed to revoke link:', err);
      toast.error('Failed to revoke link');
    } finally {
      setActionLoading(null);
      setOpenMenuId(null);
    }
  };

  // Navigate to client
  const navigateToClient = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Check if link is active
  const isLinkActive = (link: SecureUploadLink) => link.status === 'active';

  // Loading state
  if (loading && links.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[#002B45] animate-spin mr-2" />
          <span className="text-gray-600">Loading upload links...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex flex-col items-center justify-center text-red-600">
          <AlertCircle className="w-8 h-8 mb-2" />
          <span>{error}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLinks}
            className="mt-4"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-[#002B45]" />
          <h3 className="text-lg font-medium text-gray-900">Upload Links</h3>
          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
            {total}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLinks}
            disabled={loading}
            className="flex items-center gap-1.5"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </Button>
          {onCreateLink && (
            <Button
              size="sm"
              onClick={onCreateLink}
              className="flex items-center gap-1.5"
            >
              <Plus size={14} />
              Create Link
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Status:</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>
      </div>

      {/* Links Table */}
      {links.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Link2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h4 className="text-lg font-medium text-gray-900 mb-1">No upload links</h4>
          <p className="text-sm text-gray-500 mb-4">
            Create a secure link to request documents from clients.
          </p>
          {onCreateLink && (
            <Button onClick={onCreateLink} className="flex items-center gap-1.5 mx-auto">
              <Plus size={14} />
              Create Upload Link
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Doc Types
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uploads
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {links.map((link) => (
                  <tr key={link.id} className="hover:bg-gray-50">
                    {/* Client */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-400" />
                        <div>
                          <button
                            onClick={() => navigateToClient(link.client_id)}
                            className="text-sm font-medium text-gray-900 hover:text-[#002B45] hover:underline"
                          >
                            {link.client_name || 'Unknown Client'}
                          </button>
                          {link.client_email && (
                            <p className="text-xs text-gray-500">{link.client_email}</p>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Doc Types */}
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {link.document_types.slice(0, 2).map((type) => (
                          <span
                            key={type}
                            className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                          >
                            {getDocTypeLabel(type)}
                          </span>
                        ))}
                        {link.document_types.length > 2 && (
                          <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            +{link.document_types.length - 2}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Uploads Progress */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-900">
                          {link.uploads_used} / {link.max_uploads}
                        </span>
                        <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#002B45] rounded-full"
                            style={{ width: `${(link.uploads_used / link.max_uploads) * 100}%` }}
                          />
                        </div>
                      </div>
                    </td>

                    {/* Expires */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Clock size={14} />
                        {formatDate(link.expires_at)}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getUploadLinkStatusColor(link.status)}`}>
                        {getUploadLinkStatusLabel(link.status)}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Copy Link */}
                        {isLinkActive(link) && link.portal_url && (
                          <button
                            onClick={() => handleCopyUrl(link)}
                            className="p-1.5 text-gray-500 hover:text-[#002B45] hover:bg-gray-100 rounded transition-colors"
                            title="Copy link"
                          >
                            {copiedLinkId === link.id ? (
                              <Check size={14} className="text-green-600" />
                            ) : (
                              <Copy size={14} />
                            )}
                          </button>
                        )}

                        {/* More Actions */}
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenuId(openMenuId === link.id ? null : link.id)}
                            className="p-1.5 text-gray-500 hover:text-[#002B45] hover:bg-gray-100 rounded transition-colors"
                            aria-label="More actions"
                            aria-expanded={openMenuId === link.id}
                            aria-haspopup="menu"
                          >
                            <MoreVertical size={14} />
                          </button>

                          {openMenuId === link.id && (
                            <>
                              {/* Backdrop to close menu */}
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setOpenMenuId(null)}
                              />
                              {/* Dropdown Menu */}
                              <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" role="menu">
                                {isLinkActive(link) && (
                                  <>
                                    <button
                                      onClick={() => handleResendEmail(link.id)}
                                      disabled={actionLoading === link.id}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      role="menuitem"
                                    >
                                      {actionLoading === link.id ? (
                                        <Loader2 size={14} className="animate-spin" />
                                      ) : (
                                        <Mail size={14} />
                                      )}
                                      Resend Email
                                    </button>
                                    <button
                                      onClick={() => handleRevokeLink(link.id)}
                                      disabled={actionLoading === link.id}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                      role="menuitem"
                                    >
                                      {actionLoading === link.id ? (
                                        <Loader2 size={14} className="animate-spin" />
                                      ) : (
                                        <Trash2 size={14} />
                                      )}
                                      Revoke Link
                                    </button>
                                  </>
                                )}
                                {link.portal_url && (
                                  <a
                                    href={link.portal_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    role="menuitem"
                                  >
                                    <ExternalLink size={14} />
                                    Open Portal
                                  </a>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecureLinksTable;
